// src/hooks/versionWatcher.tsx
import { env } from 'config/env';
import { useEffect, useRef, useState, useCallback } from 'react';
import DefaultButtonV2 from 'components/buttonV2/defaultButtonV2';
import { useTranslation } from 'react-i18next';
import { safeLocalStorage } from 'util/safeLocalStorage';

type VersionJson = {
    version?: string;
    builtAt?: string;
};

const versionUrl = () =>
    env.versionFileUrl || `${window.location.origin}/version.json`;

const envName = () => (env.environment || 'development').trim().toLowerCase();

const lsKey = (runtimeEnv: string) => `appVersion:${runtimeEnv}`;

/** Prod: use version; Non-prod (staging): use builtAt */
function makeToken(data: VersionJson, runtimeEnv: string) {
    const v = (data.version || '').trim();
    const t = (data.builtAt || '').trim();
    return runtimeEnv === 'production' ? v : t;
}

/** Compare current vs next */
function isNewer(current: string, next: string, runtimeEnv: string) {
    if (!current) return true;
    if (!next) return false;

    if (runtimeEnv === 'production') {
        return current !== next;
    }

    const curTime = new Date(current).getTime();
    const nextTime = new Date(next).getTime();
    if (!Number.isNaN(curTime) && !Number.isNaN(nextTime)) {
        return nextTime > curTime;
    }
    return current !== next;
}

export default function VersionWatcher({
    autoReload = false, // pass true for Reception
}: {
    autoReload?: boolean;
}) {
    const { t } = useTranslation();
    const [show, setShow] = useState(false);
    const [nextToken, setNextToken] = useState<string | null>(null);

    const runtimeEnv = envName();
    const key = lsKey(runtimeEnv);
    const isDev = runtimeEnv === 'development'; // no checks in dev

    // prevent overlapping fetches
    const checkingRef = useRef(false);

    const reloadNow = useCallback(
        (token?: string) => {
            const toSave = token ?? nextToken;
            if (toSave) safeLocalStorage.setItem(key, toSave);
            setShow(false);
            window.location.reload();
        },
        [key, nextToken]
    );

    const checkNow = useCallback(async () => {
        if (isDev) return; // no network calls in development
        if (checkingRef.current) return;
        checkingRef.current = true;

        const ctrl = new AbortController();
        const timer = setTimeout(() => ctrl.abort(), 8000);

        try {
            // cache-bust to always see latest version.json
            const base = versionUrl();
            const url = `${base}${
                base.includes('?') ? '&' : '?'
            }ts=${Date.now()}`;

            const res = await fetch(url, {
                cache: 'no-cache',
                headers: { Accept: 'application/json' },
                signal: ctrl.signal,
            });
            if (!res.ok) throw new Error(`version.json ${res.status}`);

            const data: VersionJson = await res.json();
            const token = makeToken(data, runtimeEnv);

            if (!token) {
                setShow(false);
                return;
            }

            const current = safeLocalStorage.getItem(key);

            // First time -> accept remote as baseline (no banner)
            if (!current) {
                safeLocalStorage.setItem(key, token);
                setShow(false);
                return;
            }

            // Show banner (or auto-reload for Reception) only if LS is older than latest
            const newer = isNewer(current, token, runtimeEnv);
            if (newer) {
                setNextToken(token);
                if (autoReload) reloadNow(token);
                // Reception
                else setShow(true);
            } else {
                setShow(false);
            }
        } catch {
            setShow(false);
        } finally {
            clearTimeout(timer);
            checkingRef.current = false;
        }
    }, [key, runtimeEnv, isDev, autoReload, reloadNow]);

    // Initial check + activity hooks
    useEffect(() => {
        if (isDev) {
            return () => {};
        }

        checkNow();

        const onFocus = () => checkNow();
        const onVisibility = () => {
            if (document.visibilityState === 'visible') checkNow();
        };
        const onOnline = () => checkNow();

        window.addEventListener('focus', onFocus);
        document.addEventListener('visibilitychange', onVisibility);
        window.addEventListener('online', onOnline);

        return () => {
            window.removeEventListener('focus', onFocus);
            document.removeEventListener('visibilitychange', onVisibility);
            window.removeEventListener('online', onOnline);
        };
    }, [checkNow, isDev]);

    // Cross-tab: if another tab accepts (writes token), hide banner here too
    useEffect(() => {
        if (isDev) {
            return () => {};
        }

        const onStorage = (e: StorageEvent) => {
            if (e.key !== key) return;
            if (!e.newValue || e.newValue === e.oldValue) return;
            setShow(false);
        };

        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, [key, isDev]);

    // Slow periodic fallback (tab left open)
    useEffect(() => {
        if (isDev) {
            return () => {};
        }

        const id = setInterval(() => checkNow(), 5 * 60 * 1000);
        return () => clearInterval(id);
    }, [checkNow, isDev]);

    if (isDev || !show) return null;

    return (
        <div
            className="fixed inset-x-0 bottom-0 z-[9999] flex justify-center px-3 pb-3 pointer-events-none"
            aria-live="polite"
        >
            <div
                className="pointer-events-auto flex w-full max-w-md items-center rounded-lg bg-pk-oparko-dark-blue text-white shadow-md px-3 py-4"
                role="status"
            >
                <div className="flex-1 text-center text-sm md:text-base leading-snug">
                    {t('VersionWatcher')}
                </div>
                <DefaultButtonV2
                    buttonText={t('Update')}
                    onClick={() => reloadNow()}
                    bgColor="blue"
                    textColor="white"
                    textSize="text-base"
                    className="ml-3"
                />
            </div>
        </div>
    );
}
